import React, { useState, useEffect, useRef } from 'react';
import { shortLocation } from '../../../utils/locationDisplay';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  InputNumber,
  Card,
  Divider,
  Alert,
  Tag,
  Tooltip,
  message,
  Table,
  Popconfirm,
  Badge,
  Collapse,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  WarningOutlined,
  BarcodeOutlined,
  UserOutlined,
  GlobalOutlined,
  TruckOutlined,
  ProductOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { OutboundOrder, OutboundOrderItem, OutboundOrderStatus } from '../pages/OutboundOrderPage';
import apiService from '../../../services/apiService';
import { useAuthStore } from '../../../stores/authStore';
import { useTranslation } from "react-i18next";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// Feature flag: set to true to show the location/lot selection column in outbound order form
const ENABLE_LOCATION_SELECTION = true;

interface Customer {
  value: string;
  label: string;
  code: string;
}

interface Product {
  id: string;
  nameEN: string;
  nameCN: string;
  skuId: string;
  productBarcode?: string;
  brand: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  unitPrice: number;
  availableStock: number;
  defaultAllocateRule: string;
  alternateSkuList: string[]; // 添加備用 SKU 列表
  warehouses?: Array<{ warehouseId: string; warehouseName: string; availableQuantity: number }>; // 各倉庫庫存
}

interface CountryCode {
  value: string;
  label: string;
}

interface AllocateRule {
  value: string;
  label: string;
}

interface OutboundOrderFormProps {
  visible: boolean;
  order?: OutboundOrder | null;
  customers: Customer[];
  products: Product[];
  couriers: string[];
  // The built-in couriers, so the ones a user added can be told apart and
  // given a remove button. Built-ins are never removable.
  defaultCouriers: string[];
  onAddCourier: (value: string) => void;
  onRemoveCourier: (value: string) => void;
  countryCodes: CountryCode[];
  allocateRules: AllocateRule[];
  warehouses?: Array<{ id: string; name: string }>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const OutboundOrderForm: React.FC<OutboundOrderFormProps> = ({
  visible,
  order,
  customers,
  products,
  couriers,
  defaultCouriers,
  onAddCourier,
  onRemoveCourier,
  countryCodes,
  allocateRules,
  warehouses = [],
  onSubmit,
  onCancel,
}) => {
    const { t } = useTranslation();
  const [form] = Form.useForm();
  const { user: authUser } = useAuthStore();
  // CUSTOMER role users may not pick warehouse locations — backend strips these fields too.
  // Hides the column entirely so they don't see an unusable dropdown.
  const canSelectLocation = ENABLE_LOCATION_SELECTION && authUser?.role !== 'CUSTOMER';
  const [newCourierInput, setNewCourierInput] = useState('');

  // An order keeps whatever courier text it was saved with. If that courier was
  // later removed from the company list, show it anyway so editing the order
  // doesn't silently blank the field.
  const courierChoices = React.useMemo(() => {
    const existing = (order?.courier || '').trim();
    return existing && !couriers.includes(existing) ? [...couriers, existing] : couriers;
  }, [couriers, order?.courier]);

  const addCourier = () => {
    const trimmed = newCourierInput.trim();
    if (!trimmed) return;
    if (!couriers.includes(trimmed)) onAddCourier(trimmed);
    form.setFieldValue('courier', trimmed);
    setNewCourierInput('');
  };

  const [items, setItems] = useState<OutboundOrderItem[]>([]);
  const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string; customerName: string; customerCode: string }[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [hasStockIssues, setHasStockIssues] = useState(false);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [productOptions, setProductOptions] = useState<Array<{ value: string; label: string; product: Product }>>([]);
  const productSearchTimer = useRef<any>(null);
  const productSearchAbortController = useRef<AbortController | null>(null);
  const lastQueryRef = useRef<string>('');

  // 取貨庫位選擇 - 按 product-warehouse 組合快取庫位庫存
  const [inventoryRawRecords, setInventoryRawRecords] = useState<Record<string, any[]>>({});
  const [locationsLoading, setLocationsLoading] = useState<Record<string, boolean>>({});
  // Per-cache metadata from getStockByLocation: { isBundle, canonicalSku, requestedSku }.
  // Used to disable the dropdown for bundle SKUs and surface alternate-SKU redirection.
  const [inventoryMeta, setInventoryMeta] = useState<Record<string, { isBundle: boolean; canonicalSku: string; requestedSku: string }>>({});
  const isEditLoadRef = useRef(false); // Skip stock overwrite during edit load

  // 初始化表單數據
  useEffect(() => {
    if (visible) {
      if (order) {
        // 編輯模式 - clear stale items first so warehouseId effect won't overwrite backend stock
        setItems([]);
        isEditLoadRef.current = true; // Prevent warehouseId effect from overwriting backend stock
        loadOrderDetail(order.id);
      } else {
        // 新增模式
        form.resetFields();
        // 設置新增模式的默認值
        form.setFieldsValue({
          status: '待發貨',
          countryCode: 'HK', // 默認選擇香港
          createdAt: dayjs(), // 默認為當前時間
        });
        setItems([]);
        setSelectedCustomer(null);
      }
      // 預設選中第一個倉庫
      if (warehouses && warehouses.length > 0) {
        setWarehouseId(warehouses[0].id);
      }
      // 初始載入產品選項
      fetchProductOptions('');
    }
  }, [visible, order, form, customers]);

  // 載入訂單詳情
  const loadOrderDetail = async (orderId: string) => {
    try {
      const resp: any = await apiService.getOutboundOrder(orderId);
      const orderDetail: any = resp?.data || resp || {};
      
      // 填充表單數據 - resolve customer id for Select value
      const matchedCustomer = customers.find(c => c.code === orderDetail.customerCode) ||
        customers.find(c => c.label === (orderDetail.customerName || orderDetail.customerCode));
      form.setFieldsValue({
        customerName: matchedCustomer?.value || orderDetail.customerName || orderDetail.customerCode,
        customerCode: orderDetail.customerCode,
        courier: orderDetail.courier || orderDetail.deliveryCompany,
        receiver: orderDetail.receiver,
        countryCode: orderDetail.countryCode || 'HK', // 默認選擇香港
        deliveryAddress: orderDetail.deliveryAddress,
        deliveryTrackingNumber: orderDetail.deliveryTrackingNumber || orderDetail.trackingNumber,
        deliveryRemark: orderDetail.deliveryRemark || '',
        otherRemarks: orderDetail.otherRemarks || '',
        salesChannel: orderDetail.salesChannel,
        status: orderDetail.status,
        remark: orderDetail.remark,
        warehouseId: orderDetail.warehouseId || warehouseId,
        createdAt: orderDetail.createdAt ? dayjs(orderDetail.createdAt) : undefined,
        pickUpDate: (orderDetail as any).pickUpDate ? dayjs((orderDetail as any).pickUpDate) : undefined,
      });
      
      // Sync warehouseId state from order detail so cache keys match in render
      const wId = orderDetail.warehouseId || warehouseId;
      if (wId) setWarehouseId(wId);

      // 設置 items 數據
      if (Array.isArray(orderDetail.items)) {
        setItems(orderDetail.items);
        // Pre-fetch stock-by-location for items that have a SKU (so location Select can show options on edit)
        if (canSelectLocation && wId) {
          const uniqueSkus = new Set<string>();
          for (const it of orderDetail.items) {
            const sku = it.skuId || it.productSku || '';
            if (sku && !uniqueSkus.has(sku)) {
              uniqueSkus.add(sku);
              fetchStockByLocation(sku, wId);
            }
          }
        }
      } else {
        setItems([]);
      }

      setSelectedCustomer(customers.find(c => c.code === orderDetail.customerCode) || null);

      // Allow warehouseId effect to run normally again after edit load completes
      // Use setTimeout to ensure React has processed the batched state updates first
      setTimeout(() => { isEditLoadRef.current = false; }, 0);
      
    } catch (error) {
      console.error('Failed to load order details:', error);

      // Do NOT degrade into an editable form when the detail fetch fails. The
      // `order` prop comes from the list row, which carries no line items, so
      // the old fallback rendered an EMPTY item table; saving that sent
      // items: [] and the backend deleted every line of the order
      // (OUT-2026-571427 lost both of its lines this way). Closing is the safe
      // outcome: the user retries instead of unknowingly submitting an empty
      // order. loadOrderDetail is only ever called in edit mode, so there is no
      // create-mode path relying on this fallback.
      message.error(t('載入訂單詳情失敗'));
      setItems([]);
      isEditLoadRef.current = false;
      onCancel();
    }
  };

  // 檢查庫存問題
  useEffect(() => {
    const hasIssues = items.some(item => 
      item.quantityBaseUnit > (item.availableStock || 0)
    );
    setHasStockIssues(hasIssues);
  }, [items]);

  // 客戶搜索選項
  useEffect(() => {
    const options = customers.map(customer => ({
      value: customer.value,
      label: customer.label,
      customerName: customer.label,
      customerCode: customer.code,
    }));
    setCustomerOptions(options);
  }, [customers]);

  // 處理客戶選擇
  const handleCustomerSelect = (value: string) => {
    const customer = customers.find(c => c.value === value);
    if (customer) {
      setSelectedCustomer(customer);
      // Note: customerName form field holds the customer ID (Select value).
      // Ant Design Select already sets it via onChange. Only set customerCode here.
      form.setFieldValue('customerCode', customer.code);
    }
  };

  // 從後端查產品做為自動完成選項
  const mapBackendToProduct = (p: any): Product => {
    // 依當前選擇倉庫取該倉可用量
    let available = Number(p.availableStock || p.totalStock || 0);
    try {
      if (warehouseId && Array.isArray(p.warehouses)) {
        const wh = p.warehouses.find((w: any) => String(w.warehouseId || w.wid) === String(warehouseId));
        if (wh) {
          available = Number(wh.availableQuantity || wh.available || 0);
        } else {
          // If product not in selected warehouse, set to 0
          available = 0;
        }
      }
    } catch (e) {
      console.error('[OutboundForm] Error processing warehouse data:', e);
    }
    return {
      id: p.id,
      nameEN: p.itemNameEnglish || p.itemName || p.name || '',
      nameCN: p.itemNameChinese || p.itemName || '',
      skuId: p.itemCode || p.sku || '',
      productBarcode: p.barcode || '',
      brand: p.brand || '',
      length: Number(p.length || 0),
      width: Number(p.width || 0),
      height: Number(p.height || 0),
      weight: Number(p.weight || 0),
      unitPrice: Number(p.unitPrice || 0),
      availableStock: available,
      defaultAllocateRule: String(p.batchControlType || 'FIFO').toUpperCase(),
      alternateSkuList: Array.isArray(p.alternateSkuList) ? p.alternateSkuList : [], // 添加備用 SKU 列表
    } as Product;
  };

  const fetchProductOptions = async (q: string) => {
    try {
      // Cancel previous request if still pending
      if (productSearchAbortController.current) {
        console.log('[OutboundOrderForm] Aborting previous request');
        productSearchAbortController.current.abort();
      }

      console.log('[OutboundOrderForm] Fetching products with query:', q);
      lastQueryRef.current = q;
      const startTime = Date.now();

      // Use the same endpoint as InboundManagement (which works successfully)
      const resp: any = await apiService.getProductLookups({ search: q, limit: 50 });
      const duration = Date.now() - startTime;
      console.log('[OutboundOrderForm] Products fetched successfully in', duration, 'ms:', resp);
      const list: any[] = resp?.products || resp?.data?.products || [];
      console.log('[OutboundOrderForm] Raw products list:', list);

      // Map the lookup response (different structure than getProducts)
      // Backend returns: availableQuantity (total), warehouses: [{warehouseId, warehouseName, availableQuantity}]
      const mapped: Product[] = list.map((p: any) => {
        // Get warehouse-specific availability if warehouse is selected
        let available = Number(p.availableQuantity || p.availableStock || 0);
        if (warehouseId && Array.isArray(p.warehouses)) {
          const wh = p.warehouses.find((w: any) => String(w.warehouseId) === String(warehouseId));
          if (wh) {
            available = Number(wh.availableQuantity || 0);
          } else {
            // Product not in selected warehouse
            available = 0;
          }
        }
        return {
          id: p.productId || p.id,
          skuId: p.sku || p.itemCode,
          nameEN: p.name || p.itemName,
          nameCN: p.nameCN || '',
          barcodePackage: p.barcode || p.barcodePackage || '',
          barcodeBaseUnit: p.barcodeBaseUnit || '',
          brand: p.brand || '',
          length: p.length ?? null,
          width: p.width ?? null,
          height: p.height ?? null,
          weight: p.weight ?? null,
          volume: p.volume ?? null,
          unitPrice: Number(p.unitPrice || 0),
          availableStock: available,
          defaultAllocateRule: String(p.batchControlType || 'FIFO').toUpperCase(),
          alternateSkuList: Array.isArray(p.alternateSkuList) ? p.alternateSkuList : [],
          warehouses: p.warehouses || [], // Keep warehouses for later use
        } as Product;
      });
      console.log('[OutboundOrderForm] Mapped products:', mapped);
      const options = mapped.map(p => ({ value: p.skuId, label: `${p.skuId} ${p.nameEN || ''}${p.alternateSkuList?.length ? ` [${p.alternateSkuList.join(', ')}]` : ''}`.trim(), product: p }));
      console.log('[OutboundOrderForm] Setting productOptions:', options);
      setProductOptions(options);
    } catch (e) {
      console.error('[OutboundOrderForm] Failed to fetch products:', e);
      // 降級：若失敗使用傳入 products
      setProductOptions((products || []).map(p => ({ value: p.skuId, label: `${p.skuId} ${p.nameEN || ''}${p.alternateSkuList?.length ? ` [${p.alternateSkuList.join(', ')}]` : ''}`.trim(), product: p })));
    }
  };

  // 倉庫切換時，重新根據當前倉庫重算可用庫存
  useEffect(() => {
    if (visible) {
      fetchProductOptions(lastQueryRef.current || '');

      // Skip stock overwrite during edit load — detail API already provides correct availableStock
      if (isEditLoadRef.current) {
        return;
      }

      // Update available stock for existing items when warehouse changes
      if (items.length > 0 && warehouseId) {
        console.log('📦 [Warehouse Change] Starting stock update:', { warehouseId, itemCount: items.length });

        const updateItemsStock = async () => {
          try {
            // Get all unique SKUs from current items
            const skus = items.map(item => item.skuId).filter(Boolean);
            if (skus.length === 0) return;

            console.log('🔍 [Warehouse Change] Fetching stock for SKUs:', skus);

            // Fetch updated product data with warehouse context
            // Use limit: 50 to get more results and find exact match
            const promises = skus.map(sku =>
              apiService.getProducts({ limit: 50, search: sku }).catch(() => null)
            );
            const responses = await Promise.all(promises);

            // Create a map of SKU to updated available stock
            const stockMap = new Map<string, number>();
            responses.forEach((resp: any, index: number) => {
              const requestedSku = skus[index];
              if (resp?.products && Array.isArray(resp.products)) {
                // Search for exact SKU match in all returned products
                const exactMatch = resp.products.find((p: any) =>
                  (p.itemCode || p.sku) === requestedSku
                );

                if (exactMatch) {
                  const mapped = mapBackendToProduct(exactMatch);
                  stockMap.set(mapped.skuId, mapped.availableStock);
                  console.log(`✓ Exact match found for ${requestedSku}, stock: ${mapped.availableStock}`);
                } else {
                  console.warn(`✗ No exact match found for "${requestedSku}" in ${resp.products.length} results`);
                }
              }
            });

            console.log('📊 [Warehouse Change] Stock map:', Array.from(stockMap.entries()));

            // Update items with new available stock
            setItems(prevItems => {
              console.log('🔄 [Warehouse Change] Current items:', prevItems.map(i => ({ sku: i.skuId, oldStock: i.availableStock })));

              const updated = prevItems.map(item => {
                if (!item.skuId) return item;
                const newAvailableStock = stockMap.get(item.skuId);

                if (newAvailableStock !== undefined) {
                  console.log(`✅ [Warehouse Change] Updating ${item.skuId}: ${item.availableStock} → ${newAvailableStock}`);
                  return {
                    ...item,
                    availableStock: newAvailableStock,
                    isStockInsufficient: item.quantityBaseUnit > newAvailableStock
                  };
                } else {
                  console.warn(`❌ [Warehouse Change] No stock data for ${item.skuId}`);
                }
                return item;
              });

              console.log('✨ [Warehouse Change] Updated items:', updated.map(i => ({ sku: i.skuId, newStock: i.availableStock })));
              return updated;
            });
          } catch (error) {
            console.error('❌ [Warehouse Change] Error:', error);
          }
        };

        updateItemsStock();
      }
    }
  }, [warehouseId]);

  // 添加產品行項目
  const addItem = () => {
    const newItem: OutboundOrderItem = {
      id: `item_${Date.now()}`,
      productNameEN: '',
      productNameCN: '',
      skuId: '',
      productBarcode: '',
      quantityPackage: 1,
      quantityBaseUnit: 1,
      brand: '',
      length: 0,
      width: 0,
      height: 0,
      weight: 0,
      unitPrice: 0,
      allocateRule: 'FIFO',
      remark: '',
      availableStock: 0,
      isStockInsufficient: false
    };
    setItems([...items, newItem]);
  };

  // 刪除產品行項目
  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // 更新產品行項目
  const updateItem = (index: number, field: keyof OutboundOrderItem, value: any) => {
    const newItems = [...items];
    const nextItem: any = { ...newItems[index], [field]: value };

    // 如果是數量變更，同步兩個數量欄位（因為後端只儲存一個值）
    if (field === 'quantityBaseUnit' || field === 'quantityPackage') {
      nextItem.quantityBaseUnit = value;
      nextItem.quantityPackage = value;
      nextItem.isStockInsufficient = value > (nextItem.availableStock || 0);
    }

    newItems[index] = nextItem;
    setItems(newItems);
  };

  // 獲取指定 SKU + 倉庫的庫位庫存
  const fetchStockByLocation = async (productSku: string, wId: string) => {
    const cacheKey = `${wId}-${productSku}`;
    if (locationsLoading[cacheKey] || inventoryRawRecords[cacheKey]) return;
    setLocationsLoading(prev => ({ ...prev, [cacheKey]: true }));
    try {
      const response = await apiService.getStockByLocation({ productSku, warehouseId: wId });
      if (response.success) {
        setInventoryRawRecords(prev => ({ ...prev, [cacheKey]: response.data || [] }));
        if ((response as any).meta) {
          setInventoryMeta(prev => ({ ...prev, [cacheKey]: (response as any).meta }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch stock by location:', error);
    } finally {
      setLocationsLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // 處理產品選擇
  const handleProductSelect = (index: number, selected: string, option?: any) => {
    const matched = option?.product as Product | undefined;
    const product = matched || products.find(p => p.id === selected || p.nameEN === selected || p.skuId === selected);
    if (!product) return;

    // 一次性合併更新，避免多次 setState 造成舊狀態覆蓋
    const newItems = [...items];
    const prev = newItems[index] || ({} as OutboundOrderItem);

    // 檢查是否是選擇新產品（SKU改變），若是則重置數量為1，否則保留原數量
    const isNewProduct = prev.skuId && prev.skuId !== product.skuId;
    const defaultQuantity = isNewProduct ? 1 : (prev.quantityBaseUnit || 1);

    newItems[index] = {
      ...prev,
      productNameEN: product.nameEN,
      productNameCN: product.nameCN,
      skuId: product.skuId,
      productBarcode: (product.productBarcode || ''),
      brand: product.brand,
      length: product.length,
      width: product.width,
      height: product.height,
      weight: product.weight,
      unitPrice: product.unitPrice,
      availableStock: product.availableStock,
      allocateRule: product.defaultAllocateRule,
      quantityPackage: defaultQuantity,
      quantityBaseUnit: defaultQuantity,
      isStockInsufficient: defaultQuantity > (product.availableStock || 0),
      // 產品改變時清除之前的庫位選擇
      pickedFromLocation: isNewProduct ? undefined : prev.pickedFromLocation,
      pickedFromLocationId: isNewProduct ? undefined : prev.pickedFromLocationId,
      lotNumber: isNewProduct ? undefined : prev.lotNumber,
      expiryDate: isNewProduct ? undefined : prev.expiryDate,
    };
    setItems(newItems);

    // 獲取該 SKU 的庫位庫存
    if (canSelectLocation && warehouseId && product.skuId) {
      fetchStockByLocation(product.skuId, warehouseId);
    }
  };

  // 產品搜索（後端）
  const handleProductSearch = (value: string) => {
    if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
    productSearchTimer.current = setTimeout(() => fetchProductOptions(value.trim()), 300);
  };

  // 提交表單
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (items.length === 0) {
        message.error(t('請至少添加一個產品'));
        return;
      }

      // 檢查庫存問題
      const stockIssues = items.filter(item => item.isStockInsufficient);
      if (stockIssues.length > 0) {
        const issuesList = stockIssues.map(item =>
          t('{{sku}}: 出貨數量 {{qty}}, 可用庫存 {{stock}}', {
            sku: item.skuId || t('未知SKU'),
            qty: item.quantityBaseUnit,
            stock: item.availableStock || 0,
          })
        ).join('\n');
        
        Modal.confirm({
          title: t('庫存不足警告'),
          icon: <WarningOutlined style={{ color: '#faad14' }} />,
          content: (
            <div>
              <p>{t('以下產品庫存不足，是否仍要繼續提交訂單？')}</p>
              <pre style={{ 
                background: '#fff7e6', 
                padding: '12px', 
                borderRadius: '4px',
                fontSize: '12px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {issuesList}
              </pre>
              <p style={{ color: '#faad14', marginTop: '12px' }}>
                <WarningOutlined /> {t('注意：提交後訂單將被標記為庫存不足狀態')}
                                    </p>
            </div>
          ),
          okText: t('繼續提交'),
          cancelText: t('返回修改'),
          onOk: () => {
            submitOrder(values);
          },
        });
      } else {
        submitOrder(values);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const submitOrder = (values: any) => {
    const totalValue = items.reduce((sum, item) =>
      sum + (item.unitPrice * item.quantityBaseUnit), 0
    );

    // Resolve customer name from ID (customerName field holds the Select value = customer ID)
    const resolvedCustomerName = selectedCustomer?.label ||
      customers.find(c => c.value === values.customerName)?.label ||
      values.customerName;

    const orderData = {
      ...values,
      customerName: resolvedCustomerName,
      items: items,
      totalValue: totalValue,
      hasStockIssues: hasStockIssues,
      status: values.status || '待發貨' as OutboundOrderStatus,
      warehouseId: values.warehouseId || warehouseId,
      // Format with HKT timezone (UTC+8) to preserve local time
      createdAt: values.createdAt ? values.createdAt.format('YYYY-MM-DDTHH:mm:ss+08:00') : undefined,
      pickUpDate: values.pickUpDate ? values.pickUpDate.format('YYYY-MM-DDTHH:mm:ss+08:00') : null,
    };

    onSubmit(orderData);
  };

  // 產品表格列定義
  const itemColumns: ColumnsType<OutboundOrderItem> = [
    {
      title: t('產品 (用SKU選擇)'),
      key: 'productSelect',
      width: 280,
      render: (_, record, index) => (
        <Select
          showSearch
          style={{ width: 280 }}
          options={productOptions}
          placeholder={t('選擇 SKU')}
          value={record.skuId || undefined}
          optionFilterProp="label"
          filterOption={(input, option) => {
            const label = (option?.label || '').toString().toLowerCase();
            const value = (option?.value || '').toString().toLowerCase();
            return label.includes(input.toLowerCase()) || value.includes(input.toLowerCase());
          }}
          onSearch={handleProductSearch}
          onSelect={(value, option) => handleProductSelect(index, value as string, option)}
        />
      ),
    },
    {
      title: t('產品名稱 (EN)'),
      key: 'productNameEN',
      width: 220,
      render: (_, record) => (
        <Input value={record.productNameEN ? `${record.skuId ? record.skuId + ' ' : ''}${record.productNameEN}` : ''} readOnly placeholder={t('自動帶出')} />
      ),
    },
    // 隱藏產品名稱 (CN)
    // {
    //   title: '產品名稱 (CN)',
    //   key: 'productNameCN',
    //   width: 200,
    //   render: (_, record) => (
    //     <Input value={record.productNameCN} readOnly placeholder="自動帶出" />
    //   ),
    // },
    // 隱藏 SKU ID
    // {
    //   title: 'SKU ID',
    //   key: 'skuId',
    //   width: 140,
    //   render: (_, record) => (
    //     <Input value={record.skuId} readOnly placeholder="自動帶出" />
    //   ),
    // },
    {
      title: t('產品條碼'),
      key: 'productBarcode',
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.productBarcode}
          onChange={(e) => updateItem(index, 'productBarcode', e.target.value)}
          placeholder={t('產品條碼')}
          prefix={<BarcodeOutlined />}
        />
      ),
    },
    {
      title: t('包裝數量'),
      key: 'quantityPackage',
      width: 90,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          value={record.quantityPackage}
          onChange={(value) => updateItem(index, 'quantityPackage', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('可銷售數量'),
      key: 'availableStock',
      width: 100,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={record.availableStock && record.availableStock > 0 ? 'green' : 'red'}>
            {record.availableStock || 0}
          </Tag>
        </div>
      ),
    },
    ...(canSelectLocation ? [{
      title: t('取貨庫位'),
      key: 'pickedFromLocation',
      width: 250,
      render: (_: any, record: OutboundOrderItem, index: number) => {
        const cacheKey = warehouseId && record.skuId ? `${warehouseId}-${record.skuId}` : '';
        const rawRecords = cacheKey ? (inventoryRawRecords[cacheKey] || []) : [];
        const isLoadingLoc = cacheKey ? (locationsLoading[cacheKey] || false) : false;
        const meta = cacheKey ? inventoryMeta[cacheKey] : undefined;
        const isBundleSku = !!meta?.isBundle;
        const isAlternateSku = !!meta && meta.canonicalSku !== meta.requestedSku;

        // Bundle SKUs: stock is computed from components per WMS Rule 3.
        // Disable the dropdown — components are allocated independently at PROCESSING.
        if (isBundleSku) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tag color="purple" style={{ margin: 0 }}>{t('套裝產品')}</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('組件庫位於處理階段自動分配')}
                                    </Text>
            </div>
          );
        }

        // 構建選項：使用 locationId::lotNumber 複合鍵處理同庫位多批次
        const options: any[] = rawRecords
          .filter((inv: any) => !!(inv.locationId || inv.location_id))
          .map((inv: any) => {
            const locId = inv.locationId || inv.location_id;
            const fullPath = inv.fullPath || inv.full_path || locId;
            const qty = Number(inv.availableQuantity ?? inv.totalQuantity ?? 0);
            const lotNum = inv.lotNumber || inv.lot_number || '';
            const compositeKey = lotNum ? `${locId}::${lotNum}` : locId;
            return {
              value: compositeKey,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  <EnvironmentOutlined style={{ color: '#52c41a' }} />
                  <Text strong style={{ fontSize: 12 }}>{shortLocation(fullPath)}</Text>
                  <Tag color="blue" style={{ margin: 0 }}>{t('庫存:')} {qty}</Tag>
                  {lotNum && <Tag color="cyan" style={{ margin: 0 }}>{t('批次:')} {lotNum}</Tag>}
                </div>
              ),
              searchText: `${fullPath} ${lotNum}`,
            };
          });

        // 未分配庫位選項
        const whLevelRecords = rawRecords.filter((inv: any) => !(inv.locationId || inv.location_id));
        const whLevelQty = whLevelRecords.reduce(
          (sum: number, inv: any) => sum + Number(inv.availableQuantity ?? inv.totalQuantity ?? 0), 0
        );
        if (whLevelQty > 0) {
          options.unshift({
            value: '__WAREHOUSE_LEVEL__',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <WarningOutlined style={{ color: '#faad14' }} />
                <Text strong style={{ color: '#faad14', fontSize: 12 }}>{t('未分配庫位')}</Text>
                <Tag color="warning" style={{ margin: 0 }}>{t('庫存:')} {whLevelQty}</Tag>
              </div>
            ),
            searchText: '未分配庫位',
          });
        }

        // 當前選中值
        const currentValue = record.pickedFromLocationId
          ? (record.lotNumber
              ? `${record.pickedFromLocationId}::${record.lotNumber}`
              : record.pickedFromLocationId)
          : undefined;

        // If we have a saved locationId but options loaded with lot numbers (composite keys),
        // auto-correct currentValue to match the first option with the same locationId.
        // Also inject a placeholder if options haven't loaded yet.
        let resolvedValue = currentValue;
        if (currentValue && options.length > 0 && !options.find((o: any) => o.value === currentValue)) {
          // Try to find an option whose locationId prefix matches
          const matchByLocId = options.find((o: any) => {
            const optLocId = String(o.value).split('::')[0];
            return optLocId === record.pickedFromLocationId;
          });
          if (matchByLocId) {
            resolvedValue = matchByLocId.value;
          }
        }
        // If options haven't loaded yet, inject a placeholder so Select shows human-readable path
        if (resolvedValue && options.length === 0 && record.pickedFromLocation) {
          options.push({
            value: resolvedValue,
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <EnvironmentOutlined style={{ color: '#52c41a' }} />
                <Text strong style={{ fontSize: 12 }}>{shortLocation(record.pickedFromLocation)}</Text>
              </div>
            ),
            searchText: record.pickedFromLocation,
          });
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Select
              options={options}
              value={resolvedValue}
              onChange={(value) => {
                setItems(prev => {
                  const newItems = [...prev];
                  const updated = { ...newItems[index] };
                  if (!value || value === '__WAREHOUSE_LEVEL__') {
                    updated.pickedFromLocationId = value === '__WAREHOUSE_LEVEL__' ? value : '';
                    updated.pickedFromLocation = value === '__WAREHOUSE_LEVEL__' ? '未分配庫位' : '';
                    updated.lotNumber = '';
                    updated.expiryDate = '';
                  } else {
                    const [locId, lot] = value.split('::');
                    const selectedRec = rawRecords.find((r: any) => {
                      const rLocId = r.locationId || r.location_id;
                      const rLot = r.lotNumber || r.lot_number || '';
                      return rLocId === locId && rLot === (lot || '');
                    });
                    updated.pickedFromLocationId = locId;
                    updated.pickedFromLocation = selectedRec?.fullPath || selectedRec?.full_path || locId;
                    updated.lotNumber = selectedRec?.lotNumber || selectedRec?.lot_number || '';
                    updated.expiryDate = selectedRec?.expiryDate || selectedRec?.expiry_date || '';
                  }
                  newItems[index] = updated;
                  return newItems;
                });
              }}
              placeholder={
                isLoadingLoc ? t('載入中...') :
                !warehouseId ? t('請先選擇倉庫') :
                !record.skuId ? t('請先選擇產品') :
                t('選擇取貨庫位 (可選)')
              }
              allowClear
              showSearch
              optionFilterProp="searchText"
              style={{ width: '100%' }}
              disabled={!warehouseId || !record.skuId || isLoadingLoc}
              loading={isLoadingLoc}
            />
            {isAlternateSku && (
              <Tag color="geekblue" style={{ margin: 0, fontSize: 10 }}>
                {t('備用SKU → 顯示主SKU (')}{meta!.canonicalSku}{t(') 庫位')}
                                      </Tag>
            )}
          </div>
        );
      },
    }] : []),
    {
      title: t('出貨數量'),
      key: 'quantityBaseUnit',
      width: 110,
      render: (_, record, index) => (
        <div>
          <InputNumber
            min={0}
            value={record.quantityBaseUnit}
            onChange={(value) => updateItem(index, 'quantityBaseUnit', value || 0)}
            style={{ width: '100%' }}
            status={record.isStockInsufficient ? 'error' : undefined}
          />
          {record.isStockInsufficient && (
            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
              <WarningOutlined /> {t('庫存不足 (可用:')} {record.availableStock})
            </div>
          )}
        </div>
      ),
    },
    // 隱藏品牌、長度、寬度、高度、重量、單價、分配規則
    // {
    //   title: '品牌',
    //   key: 'brand',
    //   width: 100,
    //   render: (_, record, index) => (
    //     <Input
    //       value={record.brand}
    //       onChange={(e) => updateItem(index, 'brand', e.target.value)}
    //       placeholder="品牌"
    //     />
    //   ),
    // },
    // {
    //   title: '長度',
    //   key: 'length',
    //   width: 80,
    //   render: (_, record, index) => (
    //     <InputNumber
    //       min={0}
    //       step={0.1}
    //       value={record.length}
    //       onChange={(value) => updateItem(index, 'length', value || 0)}
    //       style={{ width: '100%' }}
    //       placeholder="cm"
    //     />
    //   ),
    // },
    // {
    //   title: '寬度',
    //   key: 'width',
    //   width: 80,
    //   render: (_, record, index) => (
    //     <InputNumber
    //       min={0}
    //       step={0.1}
    //       value={record.width}
    //       onChange={(value) => updateItem(index, 'width', value || 0)}
    //       style={{ width: '100%' }}
    //       placeholder="cm"
    //     />
    //   ),
    // },
    // {
    //   title: '高度',
    //   key: 'height',
    //   width: 80,
    //   render: (_, record, index) => (
    //     <InputNumber
    //       min={0}
    //       step={0.1}
    //       value={record.height}
    //       onChange={(value) => updateItem(index, 'height', value || 0)}
    //       style={{ width: '100%' }}
    //       placeholder="cm"
    //     />
    //   ),
    // },
    // {
    //   title: '重量',
    //   key: 'weight',
    //   width: 80,
    //   render: (_, record, index) => (
    //     <InputNumber
    //       min={0}
    //       step={0.1}
    //       value={record.weight}
    //       onChange={(value) => updateItem(index, 'weight', value || 0)}
    //       style={{ width: '100%' }}
    //       placeholder="kg"
    //     />
    //   ),
    // },
    // {
    //   title: '單價',
    //   key: 'unitPrice',
    //   width: 100,
    //   render: (_, record, index) => (
    //     <InputNumber
    //       min={0}
    //       step={0.01}
    //       value={record.unitPrice}
    //       onChange={(value) => updateItem(index, 'unitPrice', value || 0)}
    //       style={{ width: '100%' }}
    //       placeholder="¥"
    //     />
    //   ),
    // },
    // {
    //   title: '分配規則',
    //   key: 'allocateRule',
    //   width: 120,
    //   render: (_, record, index) => (
    //     <Select
    //       value={record.allocateRule}
    //       onChange={(value) => updateItem(index, 'allocateRule', value)}
    //       style={{ width: '100%' }}
    //     >
    //       {allocateRules.map(rule => (
    //         <Option key={rule.value} value={rule.value}>{rule.label}</Option>
    //       ))}
    //     </Select>
    //   ),
    // },
    {
      title: t('備註'),
      key: 'remark',
      width: 120,
      render: (_, record, index) => (
        <Input
          value={record.remark}
          onChange={(e) => updateItem(index, 'remark', e.target.value)}
          placeholder={t('備註')}
        />
      ),
    },
    {
      title: t('操作'),
      key: 'actions',
      width: 70,
      render: (_, record, index) => (
        <Popconfirm
          title={t('確定要刪除此產品嗎？')}
          onConfirm={() => removeItem(index)}
          okText={t('確定')}
          cancelText={t('取消')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
        title={order ? t('編輯出庫訂單') : t('新增出庫訂單')}
        open={visible}
        onCancel={onCancel}
        width={1400}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            {t('取消')}
                          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {t('確定')}
                        </Button>,
        ]}
        destroyOnClose
      >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: '待發貨',
          countryCode: 'HK',
        }}
      >
        {hasStockIssues && (
          <Alert
            message={t('庫存不足警告')}
            description={
              <div>
                <p>{t('部分產品的出貨數量超過可銷售數量。')}</p>
                <p>{t('您仍然可以繼續創建此訂單，但訂單將被標記為庫存不足狀態。')}</p>
              </div>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 基本信息 - 可折叠 */}
        <Collapse defaultActiveKey={['basic-info']} style={{ marginBottom: 16 }}>
          <Panel header={t('基本信息')} key="basic-info">
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label={t('倉庫')}
                  name="warehouseId"
                  rules={[{ required: true, message: t('請選擇倉庫') }]}
                >
                  <Select
                    placeholder={t('選擇倉庫')}
                    value={warehouseId}
                    onChange={(v) => setWarehouseId(String(v))}
                    allowClear
                    showSearch
                    filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
                  >
                    {(warehouses || []).map(w => (
                      <Option key={w.id} value={w.id}>{w.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('客戶名稱')}
                  name="customerName"
                  rules={[{ required: true, message: t('請輸入客戶名稱') }]}
                >
                  <Select
                    showSearch
                    placeholder={t('搜尋客戶名稱')}
                    filterOption={(input, option) => {
                      if (!input || !input.trim()) return true;
                      const searchText = input.toLowerCase().trim();
                      const label = String(option?.label || '');
                      return label.toLowerCase().includes(searchText);
                    }}
                    onChange={handleCustomerSelect}
                    options={customerOptions.map(option => ({
                      value: option.value,
                      label: option.label,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('客戶代碼')}
                  name="customerCode"
                  rules={[{ required: true, message: t('請選擇客戶') }]}
                >
                  <Input disabled placeholder={t('自動填充')} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('快遞商')}
                  name="courier"
                >
                  <Select
                    placeholder={t('選擇快遞商')}
                    showSearch
                    optionFilterProp="label"
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '4px 0' }} />
                        <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                          <Input
                            size="small"
                            placeholder={t('新增快遞商')}
                            value={newCourierInput}
                            onChange={(e) => setNewCourierInput(e.target.value)}
                            // Stop the Select from treating typing as search
                            // navigation while the user is naming a courier.
                            onKeyDown={(e) => e.stopPropagation()}
                            onPressEnter={addCourier}
                            style={{ flex: 1 }}
                          />
                          <Button size="small" type="text" icon={<PlusOutlined />} onClick={addCourier} />
                        </div>
                      </>
                    )}
                  >
                    {courierChoices.map(courier => (
                      <Option key={courier} value={courier} label={courier}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span>{courier}</span>
                          {!defaultCouriers.includes(courier) && (
                            <CloseCircleOutlined
                              style={{ color: '#bbb', fontSize: 12, cursor: 'pointer' }}
                              // preventDefault stops AntD committing the option
                              // as a selection when the × itself is clicked.
                              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (form.getFieldValue('courier') === courier) {
                                  form.setFieldValue('courier', undefined);
                                }
                                onRemoveCourier(courier);
                              }}
                            />
                          )}
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('銷售渠道')}
                  name="salesChannel"
                >
                  <Select placeholder={t('選擇銷售渠道')}>
                    <Option value="HKTV Mall">{t('HKTV Mall')}</Option>
                    <Option value="Shopify">Shopify</Option>
                    <Option value="Amazon">{t('Amazon')}</Option>
                    <Option value="eBay">eBay</Option>
                    <Option value="Taobao">{t('Taobao')}</Option>
                    <Option value="Tmall">{t('Tmall')}</Option>
                    <Option value="JD.com">{t('JD.com')}</Option>
                    <Option value="Pinduoduo">{t('Pinduoduo')}</Option>
                    <Option value="Lazada">{t('Lazada')}</Option>
                    <Option value="Shopee">{t('Shopee')}</Option>
                    <Option value="Direct Sales">{t('Direct Sales')}</Option>
                    <Option value="Other">{t('Other')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('收件人')}
                  name="receiver"
                >
                  <Input placeholder={t('收件人姓名')} prefix={<UserOutlined />} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label={t('訂單建立時間')}
                  name="createdAt"
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    style={{ width: '100%' }}
                    placeholder={t('選擇訂單建立時間')}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={t('預計取貨日期')}
                  name="pickUpDate"
                  // Required only when manually creating an order through this form.
                  // Imported orders (HKTVmall etc.) come in via API and bypass the
                  // form entirely, and editing an existing order shouldn't suddenly
                  // demand a field the original integration didn't populate.
                  rules={!order ? [{ required: true, message: t('請選擇預計取貨日期') }] : undefined}
                  tooltip={t('HKTVmall 訂單到達「待攬收」狀態時會自動填入；其他通路在狀態變為「待攬收」也會自動帶入今日。')}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    style={{ width: '100%' }}
                    placeholder={t('選擇預計取貨日期')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={t('國家或地區')}
                  name="countryCode"
                >
                  <Select placeholder={t('選擇國家或地區')}>
                    {countryCodes.map(country => (
                      <Option key={country.value} value={country.value}>
                        {country.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('配送追蹤號')}
                  name="deliveryTrackingNumber"
                >
                  <Input placeholder={t('追蹤號碼 (FedEx訂單可稍後生成)')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={t('配送地址')}
                  name="deliveryAddress"
                >
                  <TextArea rows={3} placeholder={t('詳細配送地址')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={t('配送備註')}
                  name="deliveryRemark"
                >
                  <TextArea rows={3} placeholder={t('配送相關備註')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={t('訂單備註')}
                  name="remark"
                >
                  <TextArea rows={3} placeholder={t('訂單備註')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={t('其他備註 (顯示於運單DN)')}
                  name="otherRemarks"
                  tooltip={t('此備註將顯示在運單(DN/Waybill)底部，以較大字體顯示')}
                >
                  <TextArea rows={3} placeholder={t('輸入需顯示在運單上的其他備註信息')} />
                </Form.Item>
              </Col>
            </Row>
          </Panel>
        </Collapse>

        {/* 產品清單 */}
        <Card 
          title={
            <Space>
              <ProductOutlined />
              {t('產品清單')}
                                        <Badge count={items.length} style={{ backgroundColor: '#52c41a' }} />
            </Space>
          }
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
              {t('添加產品')}
                              </Button>
          }
        >
          <Table
            dataSource={items}
            columns={itemColumns}
            rowKey="id"
            pagination={false}
            scroll={false}
            size="small"
            locale={{
              emptyText: t('請添加產品')
            }}
          />
          
          {items.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space size="large">
                <Text strong>
                  {t('產品總數:')} <Tag color="blue">{items.length}</Tag>
                </Text>
                <Text strong>
                  {t('總數量:')} <Tag color="green">{items.reduce((sum, item) => sum + item.quantityBaseUnit, 0)}</Tag>
                </Text>
                <Text strong>
                  {t('總金額:')} <Tag color="orange">¥{items.reduce((sum, item) => sum + (item.unitPrice * item.quantityBaseUnit), 0).toFixed(2)}</Tag>
                </Text>
                {hasStockIssues && (
                  <Text strong>
                    <Tag color="red" icon={<WarningOutlined />}>{t('庫存問題')}</Tag>
                  </Text>
                )}
              </Space>
            </div>
          )}
        </Card>
      </Form>
    </Modal>
  );
};

export default OutboundOrderForm;